/**
 * Display helpers for UAE dates. The backend stores UTC instants (e.g. an erasure date is
 * 00:00 UTC on its day); Athlyn staff read them as calendar dates in the UAE (Asia/Dubai, UTC+4,
 * no daylight saving). Missing or unparseable values render as an em dash.
 */
const DUBAI_TZ = "Asia/Dubai";

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? "";
}

/** "10 Nov 2026" — the calendar date in Asia/Dubai. */
export function formatDubaiDate(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DUBAI_TZ,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(d);
  return `${part(parts, "day")} ${part(parts, "month")} ${part(parts, "year")}`;
}

/** "10 Nov 2026, 10:05" — date and 24-hour time in Asia/Dubai. */
export function formatDubaiDateTime(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DUBAI_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  return `${formatDubaiDate(value)}, ${part(parts, "hour")}:${part(parts, "minute")}`;
}
